import { useEffect, useMemo, useState } from "react";
import { Loader2, Minus, Plus, Send, Search, Trash2, Users, ClipboardList, Printer, Receipt, ShoppingBasket } from "lucide-react";
import { StaffTakeawayDialog } from "@/components/StaffTakeawayDialog";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtMoney } from "@/lib/money";
import { printOrder } from "@/lib/printOrder";
import { getPrinterConfig, isPrinterConfigured, printTicket } from "@/lib/escpos";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { logError } from "@/lib/log";

const lineKey = (dishId, variant, options) => `${dishId}|${variant || ""}|${(options || []).join(",")}`;

export default function StaffOrders() {
  const [tables, setTables] = useState(null);
  const [menu, setMenu] = useState({ categories: [], dishes: [] });
  const [table, setTable] = useState(null);
  const [cat, setCat] = useState("all");
  const [q, setQ] = useState("");
  const [cart, setCart] = useState([]);
  const [notes, setNotes] = useState("");
  const [sending, setSending] = useState(false);
  const [restaurantName, setRestaurantName] = useState("");
  const [openOrders, setOpenOrders] = useState([]);
  const [closing, setClosing] = useState(false);
  const [lastOrder, setLastOrder] = useState(null);
  const [newTakeaway, setNewTakeaway] = useState(false);

  const load = () => Promise.all([api.get("/tables"), api.get("/categories"), api.get("/dishes"), api.get("/restaurant")])
    .then(([t, c, d, r]) => { setTables(t.data); setRestaurantName(r.data?.name || ""); setMenu({ categories: c.data, dishes: d.data.filter((x) => x.orderable && x.available !== false) }); })
    .catch((e) => { logError("Comande:", e); toast.error(formatApiError(e.response?.data?.detail)); setTables([]); });
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!table) { setOpenOrders([]); return; }
    api.get(`/tables/${table.id}/open-orders`).then((r) => setOpenOrders(r.data)).catch(() => setOpenOrders([]));
  }, [table?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const doPrint = async (o) => {
    if (isPrinterConfigured()) {
      try { await printTicket(o, restaurantName); toast.success("Comanda inviata alla stampante"); return; }
      catch (e) { toast.error(`Stampante: ${e.message}. Uso la stampa di sistema.`); }
    }
    if (!printOrder(o, restaurantName)) toast.error("Consenti le finestre popup per stampare");
  };

  const closeTable = async () => {
    if (!table) return;
    setClosing(true);
    try {
      const { data } = await api.post(`/tables/${table.id}/close`);
      toast.success(`Tavolo ${data.table} chiuso · ${data.closed_orders} comande segnate pagate alla cassa`);
      setTable(null); setCart([]); setLastOrder(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setClosing(false); }
  };

  const dishes = useMemo(() => menu.dishes.filter((d) => (cat === "all" || d.category_id === cat) && (!q || d.name.toLowerCase().includes(q.toLowerCase()))), [menu, cat, q]);
  const total = cart.reduce((s, l) => s + l.unit_price * l.quantity, 0);

  const add = (d, variant) => {
    const price = variant ? variant.price : (d.variants?.length ? Math.min(...d.variants.map((v) => v.price)) : d.price);
    const key = lineKey(d.id, variant?.label, []);
    setCart((c) => {
      const ex = c.find((l) => l.key === key);
      if (ex) return c.map((l) => (l.key === key ? { ...l, quantity: l.quantity + 1 } : l));
      const station = menu.categories.find((x) => x.id === d.category_id)?.station || "kitchen";
      return [...c, { key, dish_id: d.id, name: d.name, variant: variant?.label || null, options: [], unit_price: price, quantity: 1, notes: "", station }];
    });
  };
  const setQty = (key, delta) => setCart((c) => c.map((l) => (l.key === key ? { ...l, quantity: l.quantity + delta } : l)).filter((l) => l.quantity > 0));
  const setLineNote = (key, v) => setCart((c) => c.map((l) => (l.key === key ? { ...l, notes: v } : l)));

  const send = async () => {
    if (!table) { toast.error("Seleziona un tavolo"); return; }
    if (!cart.length) { toast.error("Aggiungi almeno un piatto"); return; }
    setSending(true);
    try {
      const sentItems = cart.map(({ key, ...l }) => l);
      const { data } = await api.post("/orders/staff", { table_id: table.id, notes, items: sentItems.map(({ station, ...l }) => l) });
      const ticket = { ...data.order, items: sentItems, total: sentItems.reduce((s, l) => s + l.unit_price * l.quantity, 0), notes };
      setLastOrder(ticket);
      const pcfg = getPrinterConfig();
      if (isPrinterConfigured() && pcfg?.autoStaff !== false) printTicket(ticket, restaurantName).catch((e) => toast.error(`Stampa automatica non riuscita: ${e.message}`));
      toast.success(data.appended ? `Piatti aggiunti alla comanda #${data.order.order_no} · Tavolo ${table.name}` : `Comanda #${data.order.order_no} inviata · Tavolo ${table.name}`, {
        action: { label: "Stampa", onClick: () => doPrint(ticket) }, duration: 8000,
      });
      setCart([]); setNotes("");
      setTable((t) => ({ ...t, status: "occupied" }));
      api.get(`/tables/${table.id}/open-orders`).then((r) => setOpenOrders(r.data)).catch(() => {});
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  if (!tables) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-[#5aa469]" /></div>;

  return (
    <div className="space-y-6" data-testid="staff-orders-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display font-bold text-3xl sm:text-4xl">Comande</h1>
          <p className="text-gray-500 mt-1">Scegli il tavolo, aggiungi i piatti e invia. Se il tavolo ha già una comanda aperta, i piatti vengono aggiunti. Pagamento alla cassa.</p>
        </div>
        <Button onClick={() => setNewTakeaway(true)} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white" data-testid="staff-new-takeaway-btn"><ShoppingBasket className="h-4 w-4 mr-1.5" />Nuovo asporto</Button>
      </div>
      <StaffTakeawayDialog open={newTakeaway} onOpenChange={setNewTakeaway} onCreated={(o) => { const pcfg = getPrinterConfig(); if (isPrinterConfigured() && pcfg?.autoStaff !== false) printTicket(o, restaurantName).catch(() => {}); }} />

      {tables.length === 0 ? (
        <div className="rounded-2xl mo-card p-10 text-center text-gray-500" data-testid="staff-no-tables">Nessun tavolo configurato: aggiungili dalla sezione Tavoli.</div>
      ) : (
        <div className="flex gap-2 flex-wrap" data-testid="staff-table-picker">
          {tables.map((t) => {
            const occ = t.status === "occupied"; const sel = table?.id === t.id;
            return (
              <button key={t.id} onClick={() => setTable(t)} className={`rounded-xl px-4 py-2 border-2 text-sm font-semibold transition-[background-color,transform] active:scale-95 ${sel ? "bg-[#1f2a24] text-white border-[#1f2a24]" : occ ? "bg-red-50 border-red-300 text-red-800" : "bg-[#5aa469]/10 border-[#5aa469] text-[#3f7a4a]"}`} data-testid={`staff-table-${t.id}`}>
                {t.name} <span className="font-normal opacity-70 inline-flex items-center gap-1 ml-1"><Users className="h-3 w-3" />{t.seats}</span>
              </button>
            );
          })}
        </div>
      )}

      <div className="grid lg:grid-cols-[1fr_360px] gap-6">
        <div className="space-y-4 order-2 lg:order-1">
          <div className="flex gap-2 flex-wrap items-center">
            <div className="relative flex-1 min-w-[200px]"><Search className="h-4 w-4 absolute left-3 top-3 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cerca piatto…" className="h-10 pl-9" data-testid="staff-search" /></div>
            <div className="flex gap-1.5 flex-wrap">
              <button onClick={() => setCat("all")} className={`rounded-full px-3 py-1.5 text-xs font-medium border ${cat === "all" ? "bg-[#5aa469] text-white border-[#5aa469]" : "bg-white border-gray-200"}`} data-testid="staff-cat-all">Tutti</button>
              {menu.categories.map((c) => <button key={c.id} onClick={() => setCat(c.id)} className={`rounded-full px-3 py-1.5 text-xs font-medium border ${cat === c.id ? "bg-[#5aa469] text-white border-[#5aa469]" : "bg-white border-gray-200"}`} data-testid={`staff-cat-${c.id}`}>{c.name}</button>)}
            </div>
          </div>
          {dishes.length === 0 ? <div className="rounded-2xl mo-card p-8 text-center text-gray-500 text-sm" data-testid="staff-no-dishes">Nessun piatto ordinabile. Attiva "Ordinabile" sui piatti nel Costruttore menù.</div> : (
            <div className="grid sm:grid-cols-2 gap-2" data-testid="staff-dish-list">
              {dishes.map((d) => (
                <div key={d.id} className="rounded-2xl mo-card p-3" data-testid={`staff-dish-${d.id}`}>
                  <div className="flex justify-between gap-2 items-start">
                    <div className="min-w-0"><p className="font-semibold truncate">{d.name}</p>{!d.variants?.length && <p className="text-sm text-gray-500">{fmtMoney(d.price)}</p>}</div>
                    {!d.variants?.length && <Button size="sm" onClick={() => add(d)} className="rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white h-8 w-8 p-0" data-testid={`staff-add-${d.id}`}><Plus className="h-4 w-4" /></Button>}
                  </div>
                  {d.variants?.length > 0 && <div className="flex gap-1.5 flex-wrap mt-2">{d.variants.map((v) => <button key={v.label} onClick={() => add(d, v)} className="rounded-full border border-gray-200 bg-white px-3 py-1 text-xs hover:border-[#5aa469]" data-testid={`staff-add-${d.id}-${v.label}`}>{v.label} · {fmtMoney(v.price)}</button>)}</div>}
                </div>
              ))}
            </div>
          )}
        </div>

        <aside className="rounded-2xl mo-card p-4 space-y-3 lg:sticky lg:top-4 self-start order-1 lg:order-2" data-testid="staff-cart">
          <div className="flex items-center justify-between"><h2 className="font-semibold inline-flex items-center gap-2"><ClipboardList className="h-4 w-4" />Comanda {table ? `· Tavolo ${table.name}` : ""}</h2>{cart.length > 0 && <button onClick={() => setCart([])} className="text-xs text-gray-400 hover:text-red-500" data-testid="staff-cart-clear">Svuota</button>}</div>
          {!table && <p className="text-xs text-amber-700 bg-amber-50 rounded-lg p-2" data-testid="staff-select-table-hint">Seleziona prima un tavolo.</p>}
          {cart.length === 0 ? <p className="text-sm text-gray-400 py-4 text-center" data-testid="staff-cart-empty">Nessun piatto aggiunto.</p> : cart.map((l) => (
            <div key={l.key} className="border-b border-gray-100 pb-2" data-testid={`staff-line-${l.key}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0"><p className="text-sm font-medium truncate">{l.name}{l.variant && <span className="text-gray-500"> ({l.variant})</span>}</p><p className="text-xs text-gray-500">{fmtMoney(l.unit_price * l.quantity)}</p></div>
                <div className="flex items-center gap-1">
                  <button onClick={() => setQty(l.key, -1)} className="h-7 w-7 rounded-full border border-gray-200 grid place-items-center" data-testid={`staff-dec-${l.key}`}>{l.quantity === 1 ? <Trash2 className="h-3 w-3 text-red-500" /> : <Minus className="h-3 w-3" />}</button>
                  <span className="w-6 text-center text-sm font-semibold" data-testid={`staff-qty-${l.key}`}>{l.quantity}</span>
                  <button onClick={() => setQty(l.key, 1)} className="h-7 w-7 rounded-full border border-gray-200 grid place-items-center" data-testid={`staff-inc-${l.key}`}><Plus className="h-3 w-3" /></button>
                </div>
              </div>
              <Input value={l.notes} onChange={(e) => setLineNote(l.key, e.target.value)} placeholder="Nota (es. senza cipolla)" className="h-8 text-xs mt-1.5" data-testid={`staff-note-${l.key}`} />
            </div>
          ))}
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Nota generale per la cucina" className="h-9 text-sm" data-testid="staff-order-notes" />
          <div className="flex items-center justify-between pt-1"><span className="text-sm text-gray-500">Totale · cassa</span><span className="font-display font-bold text-xl" data-testid="staff-total">{fmtMoney(total)}</span></div>
          <Button onClick={send} disabled={sending || !table || !cart.length} className="w-full rounded-full bg-[#5aa469] hover:bg-[#4a8a56] text-white" data-testid="staff-send-btn"><Send className="h-4 w-4 mr-2" />{sending ? "Invio…" : "Invia comanda"}</Button>
          {lastOrder && <Button variant="outline" onClick={() => doPrint(lastOrder)} className="w-full rounded-full border-gray-300 bg-white" data-testid="staff-print-last-btn"><Printer className="h-4 w-4 mr-2" />Stampa ultima comanda #{lastOrder.order_no}</Button>}

          {table && openOrders.length > 0 && (
            <div className="border-t border-gray-100 pt-3 space-y-2" data-testid="staff-open-orders">
              <p className="text-sm font-semibold inline-flex items-center gap-2"><Receipt className="h-4 w-4" />Al tavolo {table.name} · conto aperto</p>
              {openOrders.map((o) => (
                <div key={o.id} className="text-xs text-gray-600 rounded-lg bg-gray-50 p-2" data-testid={`staff-open-order-${o.id}`}>
                  <div className="flex justify-between font-medium text-gray-800"><span>#{o.order_no}</span><button onClick={() => doPrint(o)} className="text-[#4a8a56] inline-flex items-center gap-1" data-testid={`staff-print-${o.id}`}><Printer className="h-3 w-3" />Stampa</button></div>
                  {o.items.map((it, i) => <div key={i} className="flex justify-between"><span>{it.quantity}× {it.name}{it.variant ? ` (${it.variant})` : ""}</span><span>{fmtMoney(it.unit_price * it.quantity)}</span></div>)}
                </div>
              ))}
              <div className="flex items-center justify-between"><span className="text-sm text-gray-500">Conto tavolo</span><span className="font-display font-bold text-lg" data-testid="staff-table-bill">{fmtMoney(openOrders.reduce((s, o) => s + o.total, 0))}</span></div>
              <Button onClick={closeTable} disabled={closing} variant="outline" className="w-full rounded-full border-red-300 text-red-700 hover:bg-red-50 bg-white" data-testid="staff-close-table-btn">{closing ? "Chiudo…" : "Chiudi tavolo · pagato alla cassa"}</Button>
            </div>
          )}
          {table && openOrders.length === 0 && table.status === "occupied" && (
            <Button onClick={closeTable} disabled={closing} variant="ghost" className="w-full rounded-full text-red-600 hover:bg-red-50" data-testid="staff-close-table-btn">{closing ? "Chiudo…" : "Libera tavolo"}</Button>
          )}
        </aside>
      </div>
    </div>
  );
}
